<script setup>

import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from 'vue';
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";


import CourseService from '@/api/services/course.service';
import CourseSessionService from '@/api/services/courseSession.service';
import CourseRequestService from '@/api/services/courseRequest.service';

const courses = ref([{
    name: 'Nuevo curso',
    id: 0,
}])
const selectedCourse = ref();

const statusOptions = ref([
    {
        value: "requested",
        name: "Solicitada"
    },
    {
        value: "approved",
        name: "Aprobada"
    },
    {
        value: "rejected",
        name: "Rechazada"
    },
    {
        value: "assigned",
        name: "Asignada"
    },
    {
        value: "canceled",
        name: "Cancelada"
    },
])

const requestStatus = ref();

const requestNotes = ref('');
const requestedTitle = ref('');
const requestedDescription = ref('');


// eslint-disable-next-line no-undef
const emit = defineEmits(['closeDialog', 'reload'])
// eslint-disable-next-line no-undef
const props = defineProps({
    request: Object,
})


const user = computed(() => {
    return authStore.getUserLogged;
})

// eslint-disable-next-line no-unused-vars
const isAdmin = computed(() => {
    if (user.value) {
        return user.value.roles.includes('ROLE_ADMIN');
    }
    return false;
})

const seeButton = computed(() => {
    if (!isNew.value) {
        if (isAdmin.value) {
            return true;
        } else {
            return false;
        }
    } else {
        return true;
    }
})

const isNew = computed(() => {
    if (props.request) {
        return false;
    } else {
        return true;
    }
})

const params = ref({});

const authStore = useAuthStore();
const uiStore = useUiStore();

const availableSessions = ref([]);

onMounted(() => {
    if (props && props.request) {
        requestStatus.value = props.request.status;
        requestNotes.value = props.request.note;
        selectedCourse.value = props.request.course;
        requestedTitle.value = props.request.requestedTitle;
        requestedDescription.value = props.request.requestedDescription;
        if (!selectedCourse.value) {
            selectedCourse.value = courses.value[0];
        } else {
            getCourseSessions();
        }
    }
    getCourses();
})

const closeDialog = () => {
    emit('closeDialog');
}

const getCourses = () => {

    params.value = {
        'order[name]': 'asc',
        page: 1,
        itemsPerPage: 999999
    };
    uiStore.startLoading();
    CourseService.getCourses(params.value)
        .then((res) => {
            courses.value = [...courses.value, ...res.data['hydra:member']];
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

// eslint-disable-next-line no-unused-vars
const getStatusRequest = (status) => {
    switch (status) {
        case "requested":
            return "Solicitada";
        case "approved":
            return "Aprobada";
        case "rejected":
            return "Rechazada";
        case "assigned":
            return "Asignada";
        case "canceled":
            return "Cancelado";
        default:
            return "Otro";
    }
}

const updateRequest = () => {
    const paramsRequest = {
        note: requestNotes.value,
        status: requestStatus.value,
    }
    if (selectedCourse.value && selectedCourse.value.id != 0) {
        paramsRequest.course = selectedCourse.value['@id'];
    }

    CourseRequestService.updateCourseRequest(props.request.id, paramsRequest)
        .then((res) => {
            if (res.status === 201) {
                notification.addNotification({
                    message: 'Has creado tu solicitud correctamente',
                    type: 'success'
                });
            }
            uiStore.stopLoading();
            emit('reload');
            closeDialog();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

const requestCourse = () => {
    if (!isNew.value) {
        updateRequest();
        return;
    }
    const paramsRequest = {
        note: requestNotes.value,
        employee: user.value['@id']
    }
    if (selectedCourse.value && selectedCourse.value.id != 0) {
        paramsRequest.course = selectedCourse.value['@id'];
    } else {
        paramsRequest.requestedDescription = requestedDescription.value;
        paramsRequest.requestedTitle = requestedTitle.value;
    }

    CourseRequestService.createCourseRequest(paramsRequest)
        .then((res) => {
            if (res.status === 201) {
                notification.addNotification({
                    message: 'Has creado tu solicitud correctamente',
                    type: 'success'
                });
            }
            uiStore.stopLoading();
            emit('reload');
            closeDialog();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

const getCourseSessions = () => {

    const paramsReq = {
        'course.id': props.request.course.id,
        'order[capacity]': 'asc',
        page: 1,
        itemsPerPage: 999999
    }
    availableSessions.value = [];
    uiStore.startLoading();
    CourseSessionService.getCoursesSession(paramsReq)
        .then((res) => {
            const temp = res.data['hydra:member'];
            for (let i = 0; i < temp.length; i++) {
                const element = temp[i];
                if (element.availableSeats > 0) {
                    availableSessions.value.push(element);
                }
            }
            uiStore.stopLoading();
        })

}

const enrollToCourseSession = (session) => {
    if (!session) {
        notification.addNotification({
            message: 'No se ha elegido correctamente la sesion ',
            type: 'error'
        });
        return;
    }

    const paramsRequest = {
        session: session['@id']
    }

    uiStore.startLoading();
    CourseRequestService.assignCourseRequest(props.request.id, paramsRequest)
        .then((res) => {
            if (res.status === 201) {
                notification.addNotification({
                    message: 'Has inscrito correctamente ',
                    type: 'success'
                });
            }
            uiStore.stopLoading();
            emit('reload');
            closeDialog();
        })
        .catch(() => {
            uiStore.stopLoading();
        })

}

const formatDate = (input) => {
    const date = new Date(input);
    if (isNaN(date.getTime())) {
        return ''; // o devuelve algo por defecto si la fecha no es válida
    }

    const pad = (n) => String(n).padStart(2, '0');

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const mins = pad(date.getMinutes());

    return `${day}-${month}-${year} ${hours}:${mins}`;
}

</script>

<template>
    <div>
        <div class="header">
            <img style="width: 30px; margin-bottom: 14px;" src="@/assets/icons/graduating-hat-blue.svg">
            <span class="header-title pl-4">
                Solicitar Curso
            </span>
        </div>
        <div class="row f-flex">
            <div class="col-md-6 el-col-xs-24 mt-3">
                <label for="type" class="w-100">Selecciona curso</label>
                <el-select id="type" v-model="selectedCourse" filterable placeholder="Seleccione un tipo de curso"
                    :value-key="'@id'" :disabled="!isNew && !isAdmin">
                    <el-option v-for="type in courses" :key="type.id" :label="type.name" :value="type">
                    </el-option>
                </el-select>
            </div>
            <div class="col-md-6 el-col-xs-24 mt-3" v-if="request">
                <label for="status" class="w-100">Estado</label>
                <el-select id="status" v-model="requestStatus" filterable :disabled="!isNew && !isAdmin"
                    placeholder="Seleccione el estado de la sesión" :value-key="'@id'">
                    <el-option v-for="stat in statusOptions" :key="stat.value" :label="stat.name" :value="stat.value">
                    </el-option>
                </el-select>
            </div>
            <div class="f-flex col-12 el-col-xs-24 row" v-if="selectedCourse && selectedCourse.name === 'Nuevo curso'">
                <div class="col-md-6 el-col-xs-24 mt-3">
                    <label for="title" class="w-100">Titulo curso nuevo</label>
                    <el-input id="title" type="text" v-model="requestedTitle"
                        placeholder="Escribe el título del curso nuevo" style="width: 100%"
                        :disabled="!isNew && !isAdmin" />
                </div>
                <div class="col-12 el-col-xs-24 mt-3">
                    <label for="desc" class="w-100">Descripción del curso nuevo</label>
                    <el-input id="desc" type="textarea" v-model="requestedDescription"
                        placeholder="Escribe el título del curso nuevo" class="custom-textarea" :rows="5"
                        :disabled="!isNew && !isAdmin" />
                </div>
            </div>
            <div class="col-12 mt-3" v-if="selectedCourse && selectedCourse.name !== 'Nuevo curso'">
                <label for="ffdesc" class="w-100">Anotaciones sobre el curso</label>
                <el-input id="ffdesc" type="textarea" v-model="requestNotes"
                    placeholder="Escribe sobre la anotación sobre el curso." :disabled="!isNew && !isAdmin" :rows="5"
                    class="custom-textarea" />
            </div>
            <div class="col-12"
                v-if="availableSessions.length > 0 && isAdmin && (requestStatus === 'requested' || requestStatus === 'approved')">
                <label class="w-100">Sesiones disponibles</label>
                <div class="table" style="margin-top:-75px">
                    <table>
                        <thead>
                            <tr>
                                <th>Fecha inicio</th>
                                <th>Fecha fin</th>
                                <th>Localización</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="session in availableSessions" :key="session.id">
                                <td>
                                    {{ formatDate(session.startAt) }}
                                </td>

                                <td>
                                    {{ formatDate(session.endAt) }}
                                </td>
                                <td>
                                    {{ session.location }}
                                </td>
                                <td>
                                    <div class="buttons d-inline-flex">
                                        <div class="pointer">
                                            <button class="btn btn_green" @click="enrollToCourseSession(session);">
                                                Inscribir
                                            </button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="table-mobile">
                    <div v-if="availableSessions && availableSessions.length > 0">
                        <div v-for="session in availableSessions" :key="session.id">
                            <el-row class="mt-3">
                                <el-card class="card-session-item">
                                    <div class="row d-flex pointer">
                                        <div class="el-col-xs-24">
                                            <span class="session-item-span">Inicio: </span> {{
                                                formatDate(session.startAt) }}
                                        </div>
                                        <div class="el-col-xs-24">
                                            <span class="session-item-span">Fin: </span>{{ formatDate(session.endAt) }}
                                        </div>
                                        <div class="el-col-xs-24 mb-1" v-if="session && session.location">
                                            <span class="session-item-span">Localización: </span>{{ session.location }}
                                        </div>
                                        <div class="buttons d-inline-flex justify-content-end session-mobile-action">
                                            <div class="pointer">
                                                <button class="btn btn_green" @click.stop="enrollToCourseSession(session);">
                                                    Inscribir
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </el-card>
                            </el-row>
                        </div>
                    </div>
                </div>
            </div>
            <!--  Botones guardar y cancelar -->
            <div class="row footer">
                <div class="col-md-12 mt-4" style="margin-left: -10px;">
                    <div class="d-flex justify-content-end gap-3">
                        <div class="col-md-3">
                            <button class="btn btn-block btn_yellow edit-button" @click="closeDialog" style="">Cancelar
                            </button>
                        </div>
                        <div class="col-md-3">
                            <button type="submit" class="btn btn-block btn_red edit-button" @click="requestCourse"
                                v-if="seeButton" :disabled="!selectedCourse">
                                {{ isNew ? 'Solicitar' : 'Actualizar' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-title {
    font-size: 30px;
    margin-left: 5px;
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #474ce6;
}

.custom-textarea:deep(.el-textarea__inner) {
    font-size: 14px !important;
    border-radius: 20px;
}

@media(min-width: 768px) {
    .table-mobile {
        display: none;
    }
}

@media(max-width: 767px) {
    .table {
        display: none;
    }

    .courseFormRow {
        .mt-4 {
            margin-top: 0.5rem !important;
        }
    }


    .courseSessionButton {
        display: flex;
        justify-content: center;

        button {
            margin: 0px 4px 4px !important;
        }
    }

    .filter-options {
        font-size: 20px;
    }

    .card-session-item {
        padding: 10px 0px;
        border-radius: 16px;
        font-size: 15px;

        :deep(.el-card__body) {
            position: relative;
        }

        .session-item-span {
            color: $bg-blue;
            font-weight: 800;
            font-size: 16px;
            line-height: 100%;
            letter-spacing: -5%;

        }
    }

    .session-mobile-action {
        position: absolute;
        right: 12px;
        top: 10px;
        z-index: 1;
    }

    .card-session-item .row {
        padding-right: 110px;
    }
}
</style>
